import React, {useMemo, useState} from "react";
import {Alert, Box, Button, Card, DialogActions, DialogContent, Tab, Tabs} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getPackageInternalUrl} from "../../constants/appRoutes";
import ConfirmationDialog from "../confirmation-dialog";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import MaterialTable from "../material-table";
import {COLUMN_TYPES} from "./constants";
import {clearCatalog} from "../../state/catalog/catalogActions";
import {clearPackageManagerState, hideEtlWizardReport} from "../../state/package-manager/packageManagerActions";
import {etlSelector, packageManagerSelector} from "../../state/package-manager/packageManagerSelectors";

const EtlWizardReportDialog = ({columns, onDataImportSubmit}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {nodeCode, packageId} = useSelector(packageManagerSelector);
  const {isReportVisible, importResult} = useSelector(etlSelector);

  const handleClose = () => {
    dispatch(hideEtlWizardReport());
    setSelectedWarningIdx(0);
  };

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [selectedWarningIdx, setSelectedWarningIdx] = useState(0);

  const importWarnings = useMemo(() => {
    const nameToAlias = columns.reduce((acc, col) => {
      acc[col.name] = col.alias ?? col.name;
      return acc;
    }, {});

    const warnings = [];

    if ((importResult?.badRows || []).length > 0) {
      warnings.push({
        key: "badRows",
        label: t("modules.packageManager.etlWizard.report.dialog.badRows.title"),
        columns: [
          {
            field: "rowNumber",
            title: t("modules.packageManager.etlWizard.report.dialog.badRows.column.rowNumbers")
          },
          {
            field: "columnName",
            title: t("modules.packageManager.etlWizard.report.dialog.badRows.column.columnName"),
            render: ({columnName}) => nameToAlias[columnName]
          },
          {
            field: "badValue",
            title: t("modules.packageManager.etlWizard.report.dialog.badRows.column.badValue")
          }
        ],
        data: importResult.badRows
      });
    }

    if ((importResult?.fullDuplicatedRows || []).length > 0) {
      warnings.push({
        key: "fullDuplicatedRows",
        label: t("modules.packageManager.etlWizard.report.dialog.fullDuplicatedRows.title"),
        columns: [
          ...columns.map(({name, alias}) => ({
            field: name,
            title: alias
          })),
          {
            field: "rowNumbers",
            title: t("modules.packageManager.etlWizard.report.dialog.fullDuplicatedRows.column.rowNumbers")
          }
        ],
        data: importResult.fullDuplicatedRows
      });
    }

    if ((importResult?.dimensionsDuplicatedRows || []).length > 0) {
      warnings.push({
        key: "dimensionsDuplicatedRows",
        label: t("modules.packageManager.etlWizard.report.dialog.dimensionsDuplicatedRows.title"),
        columns: [
          ...columns
            .filter(({dimensionType}) => dimensionType !== COLUMN_TYPES.measure)
            .map(({name, alias}) => ({
              field: name,
              title: alias
            })),
          {
            field: "rowNumbers",
            title: t("modules.packageManager.etlWizard.report.dialog.dimensionsDuplicatedRows.column.rowNumbers")
          }
        ],
        data: importResult.dimensionsDuplicatedRows
      });
    }

    if ((importResult?.notWellDefinedCodedDimensions || []).length > 0) {
      warnings.push({
        key: "notWellDefinedCodedDimensions",
        label: t("modules.packageManager.etlWizard.report.dialog.notWellDefinedCodedDimensions.title"),
        columns: [
          {
            field: "dim",
            title: t("modules.packageManager.etlWizard.report.dialog.notWellDefinedCodedDimensions.column.dimension")
          }
        ],
        data: importResult.notWellDefinedCodedDimensions.map(dim => ({dim: dim}))
      });
    }

    if (Object.keys(importResult?.rowsWithWrongColumnsNumber || {}).length > 0) {
      warnings.push({
        key: "rowsWithWrongColumnsNumber",
        label: t("modules.packageManager.etlWizard.report.dialog.rowsWithWrongColumnsNumber.title"),
        columns: [
          {
            field: "row",
            title: t("modules.packageManager.etlWizard.report.dialog.rowsWithWrongColumnsNumber.rows")
          },
          {
            field: "columnCount",
            title: t("modules.packageManager.etlWizard.report.dialog.rowsWithWrongColumnsNumber.columnCount")
          }
        ],
        data: Object.entries(importResult.rowsWithWrongColumnsNumber).map(([row, columnCount]) => ({
          row: row,
          columnCount:
            columnCount > 0
              ? t("modules.packageManager.etlWizard.report.dialog.rowsWithWrongColumnsNumber.excessColumn", {
                  columnCount: Math.abs(columnCount)
                })
              : t("modules.packageManager.etlWizard.report.dialog.rowsWithWrongColumnsNumber.missingColumn", {
                  columnCount: Math.abs(columnCount)
                })
        }))
      });
    }

    return warnings;
  }, [columns, importResult, t]);

  return (
    importResult && (
      <CustomDialog className="etl-wizard__report-dialog" open={isReportVisible} onClose={handleClose}>
        <CustomDialogTitle onClose={handleClose}>
          {t("modules.packageManager.etlWizard.report.dialog.title")}
        </CustomDialogTitle>
        <DialogContent>
          <Box sx={{marginBottom: "16px"}}>
            {importResult?.dataImported ? (
              <Alert className="etl-wizard__report-dialog__success" severity="success">
                {t("modules.packageManager.etlWizard.report.dialog.alert.success", {rows: importResult?.importedRows})}
              </Alert>
            ) : importWarnings.length === 0 ? (
              <Alert className="etl-wizard__report-dialog__warning" severity="error">
                {t("modules.packageManager.etlWizard.report.dialog.alert.failed.noWarning")}
              </Alert>
            ) : importResult?.rowsWithErrors < importResult?.totalRows ? (
              <Alert className="etl-wizard__report-dialog__warning" severity="warning">
                {t("modules.packageManager.etlWizard.report.dialog.alert.failed.rowsWithErrors", {
                  errors: importResult?.rowsWithErrors,
                  total: importResult?.totalRows
                })}
              </Alert>
            ) : (
              <Alert className="etl-wizard__report-dialog__warning" severity="warning">
                {t("modules.packageManager.etlWizard.report.dialog.alert.failed.allRowsWithErrors")}
              </Alert>
            )}
          </Box>
          {importWarnings.length > 0 && (
            <>
              <Tabs
                value={selectedWarningIdx}
                variant="scrollable"
                scrollButtons="auto"
                onChange={(_, tabIdx) => setSelectedWarningIdx(tabIdx)}
              >
                {importWarnings.map(({key, label}, idx) => (
                  <Tab key={key} value={idx} label={label} tabIndex={0} />
                ))}
              </Tabs>
              <Card variant="outlined" sx={{height: "400px", marginTop: "16px"}}>
                <MaterialTable
                  columns={importWarnings[selectedWarningIdx].columns}
                  data={importWarnings[selectedWarningIdx].data}
                  hideSearch
                />
              </Card>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("commons.confirm.close")}</Button>
          {importResult?.dataImported ? (
            <Button
              onClick={() => {
                handleClose();
                dispatch(clearPackageManagerState());
                dispatch(clearCatalog());
                navigate(getPackageInternalUrl(nodeCode, [], packageId));
              }}
            >
              {t("modules.packageManager.etlWizard.report.dialog.actions.goToPackage")}
            </Button>
          ) : importResult?.rowsWithErrors < importResult?.totalRows ? (
            <>
              <Button onClick={() => setConfirmDialogOpen(true)}>
                {t("modules.packageManager.etlWizard.report.dialog.actions.forceImport")}
              </Button>
              <ConfirmationDialog
                title={t("modules.packageManager.etlWizard.report.dialog.confirm.title")}
                content={t("modules.packageManager.etlWizard.report.dialog.confirm.description")}
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={() => {
                  onDataImportSubmit(columns, true);
                  setConfirmDialogOpen(false);
                }}
              />
            </>
          ) : null}
        </DialogActions>
      </CustomDialog>
    )
  );
};

export default EtlWizardReportDialog;
